import { useState, useEffect, useContext } from "react";
import data from "./ProjectData";
import { theme } from "../Theme";
import styled from "styled-components";
import { PortfolioContext } from "../AppContext";
import Modal from "react-modal";
import ProjectPreview from "./ProjectPreview";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";

Modal.setAppElement("#root");
gsap.registerPlugin(ScrollTrigger, SplitText);

const ProjectTwo = () => {
	const [hoveredProject, setHoveredProject] = useState(false);
	// const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
	const [isMobile, setIsMobile] = useState(false);
	const [isTablet, setIsTablet] = useState(false);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const { setEnterProjects, setCursorLabel, cursorLabel } =
		useContext(PortfolioContext);

	useEffect(() => {
		const checkScreen = () => {
			setIsMobile(window.innerWidth <= 768);
			setIsTablet(window.innerWidth <= 1000);
		};

		checkScreen();
		window.addEventListener("resize", checkScreen);
		return () => window.removeEventListener("resize", checkScreen);
	}, []);

	// const handleMouseMove = (e) => {
	// 	setCursorPosition({ x: e.clientX - 250, y: e.clientY - 500 });
	// };

	const handleProjectTrigger = (project) => {
		if (isMobile) {
			setHoveredProject(hoveredProject?.id === project.id ? null : project);
			setModalIsOpen(true);
		}
	};

	const handleCloseModal = () => {
		setModalIsOpen(false);
	};

	const customStyles = {
		content: {
			padding: "20px",
			background: "",
			border: "none",
			borderRadius: "0",
			overflow: "auto",
			width: "400px",
		},
		overlay: {
			backgroundColor: "rgba(0, 0, 0, 0.7)",
			zIndex: 1000,
		},
	};

	useEffect(() => {
		gsap.set(".quote", { opacity: 1 });

		document.fonts.ready.then(() => {
			const containers = gsap.utils.toArray(".split");

			containers.forEach((text) => {
				const split = new SplitText(text, {
					type: "lines",
					linesClass: "line",
				});

				gsap.from(split.lines, {
					yPercent: 100,
					opacity: 0,
					stagger: 0.1,
					ease: "power3.out",
					duration: 0.6,
					scrollTrigger: {
						trigger: text,
						start: "top 85%",
						end: "bottom 40%",
						toggleActions: "play none none reverse",
						markers: false,
					},
				});
			});
		});
	}, []);

	const handleMouseEnter = () => {
		setCursorLabel("view project");
	};

	const handleMouseLeave = () => {
		setCursorLabel("");
	};

	const handleNavigate = (project) => {
		if (project?.link) {
			window.open(project.link, "_blank");
		}
	};

	return (
		<Div
			// onMouseMove={handleMouseMove}
			data-bg="red"
			onMouseEnter={() => setEnterProjects(true)}
			onMouseLeave={() => setEnterProjects(false)}
		>
			<ProjectContainer>
				<Header>(PROJECTS)</Header>
				<MainContainer>
					{data.map((project, i) => (
						<ProjectTitle
							key={i}
							onMouseEnter={() => {
								if (!isMobile && !isTablet) {
									setHoveredProject(project);
									handleMouseEnter();
								}
							}}
							onMouseLeave={() => {
								if (!isMobile && !isTablet) {
									setHoveredProject(null);
									handleMouseLeave();
								}
							}}
							onClick={() => {
								if (isMobile) {
									handleProjectTrigger(project);
								} else {
									handleNavigate(project);
								}
							}}
						>
							<InnerContainer className="container">
								<p className="split">{project.title}</p>
								<span className="split">{project.num}</span>
							</InnerContainer>
						</ProjectTitle>
					))}
				</MainContainer>

				{isMobile && isTablet && hoveredProject && modalIsOpen && (
					<Modal
						isOpen={modalIsOpen}
						onRequestClose={handleCloseModal}
						style={customStyles}
					>
						<ProjectPreview
							project={hoveredProject}
							onClose={() => setHoveredProject(null)}
							isMobile={true}
							isTablet={true}
						/>
					</Modal>
				)}

				{!isMobile && !isTablet && hoveredProject && (
					<ProjectPreview
						project={hoveredProject}
						onClose={() => setHoveredProject(null)}
						isMobile={false}
						isTablet={false}
					/>
				)}
			</ProjectContainer>
		</Div>
	);
};

export default ProjectTwo;

const Div = styled.div`
	height: 100vh;
	width: 100vw;
	color: var(--v1);
	padding: 40px;
	background-color: var(--v2);
	will-change: transform;
`;

const Header = styled.h1`
	padding-top: 15px;
	font-size: 75px;
	font-family: "Darline Serif", serif;
	letter-spacing: -6px;

	@media (max-width: ${theme.breakpoints.md}) {
		letter-spacing: -3px;
		font-size: 35px;
	}

	@media (max-width: ${theme.breakpoints.sm}) {
		letter-spacing: -3px;
		font-size: 55px;
		text-align: center;
	}
`;

const MainContainer = styled.div`
	width: 60vw;
	height: 70vh;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;

	@media (max-width: ${theme.breakpoints.sm}) {
		width: 75vw;
		height: 80vh;
	}
`;

const ProjectContainer = styled.div`
	color: var(--v1);
	border-radius: 10px;

	@media (max-width: ${theme.breakpoints.sm}) {
		padding: 0 10px;
	}
`;

const ProjectTitle = styled.div`
	height: 60px;
	padding: 20px 10px;
	transition: all 0.6s ease;
	border-radius: 10px;

	&:hover {
		font-style: italic;
		background-color: var(--v4);
		color: var(--v2);
		transition: all 0.3s ease-in-out;
	}

	@media (max-width: ${theme.breakpoints.sm}),
		(max-width: ${theme.breakpoints.md}) {
		padding: 5px 10px;
		height: 30px;
	}
`;

const InnerContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	font-family: "Poppins", sans-serif;
	text-align: center;

	p {
		letter-spacing: -2px;
		margin: 0;
		line-height: 0.7;
		font-size: 38px;
	}

	span {
		font-size: 25px;
		font-style: italic;
	}

	@media (max-width: ${theme.breakpoints.md}) {
		p {
			letter-spacing: -1px;
			margin: 0;
			font-size: 20px;
		}

		span {
			font-size: 15px;
		}
	}

	@media (max-width: ${theme.breakpoints.sm}) {
		p {
			letter-spacing: -1px;
			margin: 0;
			font-size: 18px;
		}

		span {
			font-size: 15px;
		}
	}
`;
